import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { useSyncCode } from "@/hooks/useSyncCode";

export default function TransferPage() {
  const navigate = useNavigate();
  const { redeemCode } = useSyncCode();

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  function formatInput(value: string) {
    // Auto-format to XXXX-XXXX as user types
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    if (clean.length > 4) return clean.slice(0, 4) + "-" + clean.slice(4);
    return clean;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    const cleaned = code.replace(/[^A-Z0-9]/gi, "");
    if (cleaned.length < 8) {
      setMsg({ type: "error", text: "Please enter the full 8-character code." });
      return;
    }

    setLoading(true);
    const { error } = await redeemCode(code);
    setLoading(false);

    if (error) {
      setMsg({ type: "error", text: error });
    } else {
      setMsg({ type: "success", text: "Transfer successful! Loading your data..." });
      setTimeout(() => navigate("/"), 1500);
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-14">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <span className="text-4xl">🍎</span>
            <p className="text-white font-semibold mt-2 text-sm tracking-wide">AppleSMP Bible</p>
          </div>

          <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-7">
            <div className="mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-full mb-3">
                <i className="ri-smartphone-line text-[#ef4444] text-lg" />
              </div>
              <h2 className="text-white font-semibold text-base mb-1">Transfer to this Device</h2>
              <p className="text-[#555] text-sm leading-relaxed">
                Enter the sync code from your other device to bring your highlights, notes, and progress here.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">
                  Sync Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(formatInput(e.target.value))}
                  placeholder="ABCD-1234"
                  maxLength={9}
                  className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-base font-mono tracking-widest rounded-md px-3 py-3 text-center focus:outline-none focus:border-[#ef4444] placeholder-[#333]"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
                <p className="text-[#333] text-xs mt-1.5 text-center">
                  Find your code in Settings → Sync & Transfer on your other device
                </p>
              </div>

              {msg && (
                <p
                  className={`text-xs rounded-md px-3 py-2 leading-relaxed ${
                    msg.type === "success"
                      ? "text-[#22c55e] bg-[#22c55e]/10 border border-[#22c55e]/20"
                      : "text-[#ef4444] bg-[#ef4444]/10 border border-[#ef4444]/20"
                  }`}
                >
                  {msg.text}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || code.replace(/[^A-Z0-9]/gi, "").length < 8}
                className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white text-sm font-medium py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading && <i className="ri-loader-4-line animate-spin" />}
                Transfer My Data
              </button>
            </form>

            <div className="mt-5 pt-4 border-t border-[#1a1a1a]">
              <p className="text-[#333] text-xs leading-relaxed text-center">
                Your current device&apos;s data will be replaced with the data from the other device.
              </p>
            </div>
          </div>

          <div className="text-center mt-4">
            <Link to="/" className="text-[#444] hover:text-[#888] text-xs cursor-pointer transition-colors">
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
